import React, { useState, useEffect } from 'react';
import {
    Button,
    Header,
    Icon,
    List,
    Modal,
    Image,
    Grid,
    Segment,
    Transition
} from 'semantic-ui-react';

/**
 * A modal component that encourages users to subscribe to premium features
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Function} props.onContinue - Function to call when the user clicks "Continue with Limited Access"
 * @param {boolean} props.isSecondaryThreshold - Whether this modal is showing for the secondary threshold
 * @param {string} props.current_locale - The current locale of the application
 * @returns {JSX.Element} The subscription modal component
 */
const SubscriptionModal = ({ open, onClose, onContinue, isSecondaryThreshold, current_locale = 'en' }) => {
    // Localization data for all supported languages
    const translations = {
        en: {
            upgradeExperience: 'Upgrade Your Experience',
            labExperienceAwaits: 'Your Lab Experience Awaits!',
            unlockFeatures: 'Unlock premium features for innovators like you!',
            premiumBenefits: 'Premium Benefits',
            unlimitedLabTime: 'Unlimited Lab Time',
            noTimeRestrictions: 'No time restrictions',
            saveUnlimitedCircuits: 'Save Unlimited Circuits',
            neverLoseWork: 'Never lose your work',
            allComponents: 'All Components',
            fullComponentLibrary: 'Full component library',
            prioritySupport: 'Priority Support',
            helpWhenNeeded: 'Help when you need it',
            testimonial: 'Upgrading was the best decision for my projects!',
            testimonialAuthor: 'Sarah K., Engineering Student',
            continueWithLimited: 'Continue with Limited Access',
            upgradeNow: 'Upgrade Now'
        },
        ar: {
            upgradeExperience: 'ترقية تجربتك',
            labExperienceAwaits: 'تجربة المختبر بانتظارك!',
            unlockFeatures: 'افتح الميزات المتميزة للمبتكرين مثلك!',
            premiumBenefits: 'مزايا متميزة',
            unlimitedLabTime: 'وقت مختبر غير محدود',
            noTimeRestrictions: 'لا قيود زمنية',
            saveUnlimitedCircuits: 'حفظ دوائر غير محدودة',
            neverLoseWork: 'لن تفقد عملك أبدًا',
            allComponents: 'جميع المكونات',
            fullComponentLibrary: 'مكتبة مكونات كاملة',
            prioritySupport: 'دعم ذو أولوية',
            helpWhenNeeded: 'المساعدة عند الحاجة',
            testimonial: 'كانت الترقية أفضل قرار لمشاريعي!',
            testimonialAuthor: 'سارة ك.، طالبة هندسة',
            continueWithLimited: 'المتابعة بوصول محدود',
            upgradeNow: 'الترقية الآن'
        },
        de: {
            upgradeExperience: 'Verbessern Sie Ihr Erlebnis',
            labExperienceAwaits: 'Ihr Labor-Erlebnis wartet!',
            unlockFeatures: 'Schalten Sie Premium-Funktionen für Innovatoren wie Sie frei!',
            premiumBenefits: 'Premium-Vorteile',
            unlimitedLabTime: 'Unbegrenzte Laborzeit',
            noTimeRestrictions: 'Keine Zeitbeschränkungen',
            saveUnlimitedCircuits: 'Speichern Sie unbegrenzte Schaltkreise',
            neverLoseWork: 'Verlieren Sie nie Ihre Arbeit',
            allComponents: 'Alle Komponenten',
            fullComponentLibrary: 'Vollständige Komponentenbibliothek',
            prioritySupport: 'Prioritäts-Support',
            helpWhenNeeded: 'Hilfe, wenn Sie sie brauchen',
            testimonial: 'Das Upgrade war die beste Entscheidung für meine Projekte!',
            testimonialAuthor: 'Sarah K., Ingenieurstudentin',
            continueWithLimited: 'Mit eingeschränktem Zugriff fortfahren',
            upgradeNow: 'Jetzt upgraden'
        },
        fr: {
            upgradeExperience: 'Améliorez Votre Expérience',
            labExperienceAwaits: 'Votre Expérience de Laboratoire Vous Attend!',
            unlockFeatures: 'Débloquez des fonctionnalités premium pour les innovateurs comme vous!',
            premiumBenefits: 'Avantages Premium',
            unlimitedLabTime: 'Temps de Laboratoire Illimité',
            noTimeRestrictions: 'Pas de restrictions de temps',
            saveUnlimitedCircuits: 'Enregistrez des Circuits Illimités',
            neverLoseWork: 'Ne perdez jamais votre travail',
            allComponents: 'Tous les Composants',
            fullComponentLibrary: 'Bibliothèque complète de composants',
            prioritySupport: 'Support Prioritaire',
            helpWhenNeeded: 'De l\'aide quand vous en avez besoin',
            testimonial: 'La mise à niveau était la meilleure décision pour mes projets!',
            testimonialAuthor: 'Sarah K., Étudiante en Ingénierie',
            continueWithLimited: 'Continuer avec un Accès Limité',
            upgradeNow: 'Mettre à Niveau Maintenant'
        },
        es: {
            upgradeExperience: 'Mejora Tu Experiencia',
            labExperienceAwaits: '¡Tu Experiencia de Laboratorio te Espera!',
            unlockFeatures: '¡Desbloquea funciones premium para innovadores como tú!',
            premiumBenefits: 'Beneficios Premium',
            unlimitedLabTime: 'Tiempo de Laboratorio Ilimitado',
            noTimeRestrictions: 'Sin restricciones de tiempo',
            saveUnlimitedCircuits: 'Guarda Circuitos Ilimitados',
            neverLoseWork: 'Nunca pierdas tu trabajo',
            allComponents: 'Todos los Componentes',
            fullComponentLibrary: 'Biblioteca completa de componentes',
            prioritySupport: 'Soporte Prioritario',
            helpWhenNeeded: 'Ayuda cuando la necesites',
            testimonial: '¡Actualizar fue la mejor decisión para mis proyectos!',
            testimonialAuthor: 'Sarah K., Estudiante de Ingeniería',
            continueWithLimited: 'Continuar con Acceso Limitado',
            upgradeNow: 'Actualizar Ahora'
        },
        tr: {
            upgradeExperience: 'Deneyiminizi Yükseltin',
            labExperienceAwaits: 'Laboratuvar Deneyiminiz Sizi Bekliyor!',
            unlockFeatures: 'Sizin gibi yenilikçiler için premium özelliklerin kilidini açın!',
            premiumBenefits: 'Premium Avantajlar',
            unlimitedLabTime: 'Sınırsız Laboratuvar Süresi',
            noTimeRestrictions: 'Zaman kısıtlaması yok',
            saveUnlimitedCircuits: 'Sınırsız Devre Kaydedin',
            neverLoseWork: 'Çalışmalarınızı asla kaybetmeyin',
            allComponents: 'Tüm Bileşenler',
            fullComponentLibrary: 'Tam bileşen kütüphanesi',
            prioritySupport: 'Öncelikli Destek',
            helpWhenNeeded: 'İhtiyacınız olduğunda yardım',
            testimonial: 'Yükseltme, projelerim için en iyi karardı!',
            testimonialAuthor: 'Sarah K., Mühendislik Öğrencisi',
            continueWithLimited: 'Sınırlı Erişimle Devam Et',
            upgradeNow: 'Şimdi Yükselt'
        },
        th: {
            upgradeExperience: 'อัปเกรดประสบการณ์ของคุณ',
            labExperienceAwaits: 'ประสบการณ์ห้องแล็บรอคุณอยู่!',
            unlockFeatures: 'ปลดล็อกคุณสมบัติพรีเมียมสำหรับนักนวัตกรรมเช่นคุณ!',
            premiumBenefits: 'สิทธิประโยชน์พรีเมียม',
            unlimitedLabTime: 'เวลาห้องแล็บไม่จำกัด',
            noTimeRestrictions: 'ไม่มีข้อจำกัดด้านเวลา',
            saveUnlimitedCircuits: 'บันทึกวงจรไม่จำกัด',
            neverLoseWork: 'ไม่สูญเสียงานของคุณ',
            allComponents: 'ส่วนประกอบทั้งหมด',
            fullComponentLibrary: 'ไลบรารีส่วนประกอบเต็มรูปแบบ',
            prioritySupport: 'การสนับสนุนที่มีความสำคัญ',
            helpWhenNeeded: 'ช่วยเหลือเมื่อคุณต้องการ',
            testimonial: 'การอัปเกรดเป็นการตัดสินใจที่ดีที่สุดสำหรับโครงการของฉัน!',
            testimonialAuthor: 'ซาร่าห์ เค., นักศึกษาวิศวกรรม',
            continueWithLimited: 'ดำเนินการต่อด้วยการเข้าถึงแบบจำกัด',
            upgradeNow: 'อัปเกรดเดี๋ยวนี้'
        },
        mx: {
            upgradeExperience: 'Mejora Tu Experiencia',
            labExperienceAwaits: '¡Tu Experiencia de Laboratorio te Espera!',
            unlockFeatures: '¡Desbloquea funciones premium para innovadores como tú!',
            premiumBenefits: 'Beneficios Premium',
            unlimitedLabTime: 'Tiempo de Laboratorio Ilimitado',
            noTimeRestrictions: 'Sin restricciones de tiempo',
            saveUnlimitedCircuits: 'Guarda Circuitos Ilimitados',
            neverLoseWork: 'Nunca pierdas tu trabajo',
            allComponents: 'Todos los Componentes',
            fullComponentLibrary: 'Biblioteca completa de componentes',
            prioritySupport: 'Soporte Prioritario',
            helpWhenNeeded: 'Ayuda cuando la necesites',
            testimonial: '¡Actualizar fue la mejor decisión para mis proyectos!',
            testimonialAuthor: 'Sarah K., Estudiante de Ingeniería',
            continueWithLimited: 'Continuar con Acceso Limitado',
            upgradeNow: 'Actualizar Ahora'
        }
    };
    
    // Get the correct translations based on locale, fallback to English
    const t = translations[current_locale] || translations['en'];
    const [activeFeature, setActiveFeature] = useState(0);
    const [visible, setVisible] = useState(false);
    
    // Features to highlight with animations
    const features = [
        { icon: 'clock', title: t.unlimitedLabTime, description: t.noTimeRestrictions },
        { icon: 'save', title: t.saveUnlimitedCircuits, description: t.neverLoseWork },
        { icon: 'microchip', title: t.allComponents, description: t.fullComponentLibrary },
        { icon: 'headphones', title: t.prioritySupport, description: t.helpWhenNeeded }
    ];
    
    // Rotate through features
    useEffect(() => {
        if (open) {
            setVisible(true);
            const interval = setInterval(() => {
                setActiveFeature((prev) => (prev + 1) % features.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [open]);
    
    // Handle continue with limited access
    const handleContinue = () => {
        if (onContinue) {
            onContinue();
        }
        onClose();
    };
    
    // Handle modal close with animation
    const handleClose = () => {
        setVisible(false);
        setTimeout(() => onClose(), 300);
    };
    return (
        <Modal
            open={open}
            dimmer="blurring"
            size="tiny"
            className="subscription-modal"
            closeOnDimmerClick={!isSecondaryThreshold}
            onClose={handleClose}
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '90vh'
            }}
        >
            <Modal.Header style={{ 
                background: 'linear-gradient(135deg, #f2711c 0%, #e67e22 100%)',
                color: 'white',
                padding: '1.2em',
                borderBottom: 'none'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="star" size="large" style={{ color: '#FFD700', marginRight: '10px' }} />
                    <span style={{ fontSize: '1.3em', fontWeight: '600' }}>{t.upgradeExperience}</span>
                </div>
            </Modal.Header>
            <Modal.Content style={{ padding: '0', maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Hero Section with Illustration - More compact */}
                <div style={{ 
                    background: 'linear-gradient(180deg, #fff5e6 0%, #ffffff 100%)',
                    padding: '15px',
                    textAlign: 'center'
                }}>
                    <Transition visible={visible} animation='pulse' duration={500}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ 
                                width: '60px', 
                                height: '60px', 
                                background: 'linear-gradient(135deg,rgb(252, 124, 20) 0%, #f2711c 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(154, 45, 5, 0.3)',
                                marginRight: '15px'
                            }}>
                                <Icon name="lab" size="big" style={{ color: 'white', margin: 0 }} />
                            </div>
                            
                            <div style={{ textAlign: 'left' }}>
                                <Header as="h2" style={{ 
                                    margin: '0 0 5px', 
                                    color: '#1a1a1a',
                                    fontSize: '22px',
                                    fontWeight: '700'
                                }}>
                                    {t.labExperienceAwaits}
                                </Header>
                                
                                <p style={{ 
                                    fontSize: '14px', 
                                    lineHeight: '1.4', 
                                    margin: '0',
                                    color: '#444'
                                }}>
                                    {t.unlockFeatures}
                                </p>
                            </div>
                        </div>
                    </Transition>
                </div>
                
                {/* Feature Highlight Section - More compact */}
                
                <div style={{ padding: '15px' }}>
                    <Header as="h3" style={{ 
                        color: '#f2711c', 
                        fontSize: '16px',
                        margin: '0 0 10px',
                        fontWeight: '600',
                        textAlign: 'center'
                    }}>
                        <Icon name="gem outline" /> {t.premiumBenefits}
                    </Header>
                    
                    <Grid stackable columns={2} style={{ margin: '0' }}>
                        {features.map((feature, index) => (
                            <Grid.Column key={index} style={{ padding: '5px' }}>
                                <Transition
                                    visible={activeFeature === index}
                                    animation='glow'
                                    duration={800}
                                >
                                    <Segment 
                                        raised={activeFeature === index}
                                        style={{ 
                                            padding: '10px', 
                                            height: '100%',
                                            borderRadius: '8px',
                                            border: activeFeature === index ? '1px solid #f2711c' : '1px solid #e0e0e0',
                                            background: activeFeature === index ? '#f7fbff' : 'white',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Icon 
                                                name={feature.icon} 
                                                style={{ 
                                                    color: activeFeature === index ? '#f2711c' : '#666',
                                                    marginRight: '8px'
                                                }} 
                                            />
                                            <div>
                                                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>
                                                    {feature.title}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    {feature.description}
                                                </div>
                                            </div>
                                        </div>
                                    </Segment>
                                </Transition>
                            </Grid.Column>
                        ))}
                    </Grid>
                    
                    {/* Testimonial/Social Proof - More compact */}
                    <div style={{ 
                        background: '#f9f9f9', 
                        borderRadius: '8px',
                        padding: '10px',
                        margin: '10px 0 0',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <div style={{ 
                            minWidth: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            background: '#e0e1e2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '10px'
                        }}>
                            <Icon name="user" size="small" style={{ margin: 0 }} />
                        </div>
                        <div>
                            <p style={{ 
                                fontSize: '12px', 
                                fontStyle: 'italic',
                                margin: '0 0 2px',
                                color: '#555'
                            }}>
                                "{t.testimonial}"
                            </p>
                            <p style={{ fontSize: '11px', margin: 0, color: '#777' }}>— {t.testimonialAuthor}</p>
                        </div>
                    </div>
                </div>
            </Modal.Content>
            <Modal.Actions style={{ 
                background: '#f8f9fa',
                borderTop: '1px solid #eee',
                padding: '12px 15px',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                position: 'sticky',
                bottom: 0
            }}>
                {!isSecondaryThreshold && (
                    <Button
                        basic
                        style={{
                            margin: '5px',
                            boxShadow: 'none',
                            color: '#666',
                            fontWeight: 'normal'
                        }}
                        onClick={handleContinue}
                    >
                        {t.continueWithLimited}
                    </Button>
                )}
                <Button
                    style={{
                        margin: '5px',
                        background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                        color: 'white',
                        padding: '12px 20px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        boxShadow: '0 4px 10px rgba(33, 133, 208, 0.3)',
                        transition: 'all 0.2s ease'
                    }}
                    icon
                    labelPosition="right"
                    onClick={() => {
                        window.open(`/${current_locale}/pricing-table`, '_blank');
                        handleClose();
                    }}
                >
                    {t.upgradeNow}
                    <Icon name="arrow right" />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default SubscriptionModal;